import React, { useRef, useState } from "react";
import styled from "styled-components";
import emailjs from "@emailjs/browser";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  background-color: transparent;
  padding: 40px 16px;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.white};
  margin-bottom: 2rem;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: -0.02em;
  align-self: flex-start;

  &::before {
    content: "~/ ";
    color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 700px;
  background: ${({ theme }) => theme.card};
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  margin-top: 20px;
`;

const WindowControls = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #252526;
  border-bottom: 1px solid #333;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const ControllerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: #888;
`;

const FormBody = styled.form`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;

  @media (max-width: 640px) {
    padding: 1.5rem;
  }
`;

const IntroText = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-family: 'JetBrains Mono', monospace;
  color: ${({ theme }) => theme.primary};
  font-size: 0.9rem;
  margin-left: 0.2rem;
`;

const Input = styled.input`
  background: #121212;
  border: 1px solid #444;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 4px;
  padding: 0.8rem 1rem;
  font-family: inherit;
  font-size: 1rem;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 1px rgba(139, 195, 74, 0.15);
  }
`;

const TextArea = styled.textarea`
  background: #121212;
  border: 1px solid #444;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 4px;
  padding: 0.8rem 1rem;
  font-family: inherit;
  font-size: 1rem;
  transition: all 0.2s ease;
  resize: vertical;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 1px rgba(139, 195, 74, 0.15);
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  align-self: flex-start;
  background-color: ${({ theme }) => theme.primary};
  color: #121212;
  border: 1px solid ${({ theme }) => theme.primary};
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-height: 3rem;

  &:hover:not(:disabled) {
    background-color: #9ccc65;
    border-color: #9ccc65;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 520px) {
    align-self: stretch;
    width: 100%;
  }
`;

const Contact = () => {
  const form = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [sentStatus, setSentStatus] = useState(null); // 'success', 'error'

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSentStatus(null);

    emailjs
      .sendForm(
        "service_tox7kqs",
        "template_nv7k7mj",
        form.current,
        "SybVGsYS52j2TfLbi"
      )
      .then(
        () => {
          setSubmitting(false);
          setSentStatus("success");
          form.current.reset();
          setTimeout(() => setSentStatus(null), 4000);
        },
        (error) => {
          setSubmitting(false);
          setSentStatus("error");
          setTimeout(() => setSentStatus(null), 4000);
        }
      );
  };

  return (
    <Container id="Contact">
      <Wrapper>
        <SectionTitle>Contact.sh</SectionTitle>
        
        <FormContainer>
          <WindowControls>
            <Dot $color="#ff5f56" />
            <Dot $color="#ffbd2e" />
            <Dot $color="#27c93f" />
          </WindowControls>
          
          <ControllerHeader>
            <svg fill="none" height="14" stroke="#888" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" width="14">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            ContactController.js
          </ControllerHeader>

          <FormBody ref={form} onSubmit={handleSubmit}>
            <IntroText>
              Want to connect or have an opportunity? POST your details below and I will get back to you soon.
            </IntroText>
            
            <FormGroup>
              <Label htmlFor="from_name">Name</Label>
              <Input 
                id="from_name"
                name="from_name" 
                placeholder="John Doe" 
                required 
                type="text" 
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="from_email">Email</Label>
              <Input 
                id="from_email"
                name="from_email" 
                placeholder="john@example.com" 
                required 
                type="email" 
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input 
                id="subject"
                name="subject" 
                placeholder="API Collaboration" 
                required 
                type="text" 
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea 
                id="message"
                name="message" 
                placeholder="Your message body here..." 
                required 
                rows={5} 
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting && "Executing..."}
              {!submitting && sentStatus === "success" && (
                <span style={{ color: "var(--spring-green, #8bc34a)", fontFamily: "'JetBrains Mono', monospace", fontWeight: "bold" }}>
                  [200 OK] Message Sent
                </span>
              )}
              {!submitting && sentStatus === "error" && (
                <span style={{ color: "#ff5f56", fontFamily: "'JetBrains Mono', monospace", fontWeight: "bold" }}>
                  [500] Network Error
                </span>
              )}
              {!submitting && !sentStatus && (
                <>
                  <svg fill="none" height="18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="18">
                    <line x1="22" x2="11" y1="2" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                  POST Request
                </>
              )}
            </SubmitButton>
          </FormBody>
        </FormContainer>
      </Wrapper>
    </Container>
  );
};

export default Contact;
